import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Avatar,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { PickerPopover } from "./PickerPopover";
import { PickerRow } from "./PickerRow";
import { INSERT_OFFER_COMMAND } from "../../extensions/offers/editor/offerCommands";
import type { OfferDefinition } from "../types";

import { SearchIcon as Search } from "@/icons";

function OfferSourceBadge({ isStatic }: { isStatic: boolean }) {
  return (
    <Avatar
      sx={(theme) => ({
        width: 24,
        height: 24,
        bgcolor: theme.palette.background.default,
        border: "2px solid",
        borderColor: isStatic
          ? theme.palette.info.main
          : theme.palette.warning.main,
        color: isStatic ? theme.palette.info.main : theme.palette.warning.main,
        fontSize: 12,
        fontWeight: 700,
      })}
    >
      {isStatic ? "S" : "U"}
    </Avatar>
  );
}

interface OfferPickerPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  offers: OfferDefinition[];
  /** When provided, called instead of dispatching INSERT_OFFER_COMMAND. */
  onSelectOffer?: (offer: OfferDefinition) => void;
}

export function OfferPickerPopover({
  anchorEl,
  onClose,
  offers,
  onSelectOffer,
}: OfferPickerPopoverProps) {
  const [editor] = useLexicalComposerContext();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return offers;
    return offers.filter(
      (o) =>
        o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q),
    );
  }, [offers, search]);

  const handleSelect = useCallback(
    (offer: OfferDefinition) => {
      onClose();
      setSearch("");
      if (onSelectOffer) {
        onSelectOffer(offer);
        return;
      }
      editor.focus(() => {
        editor.dispatchCommand(INSERT_OFFER_COMMAND, {
          slug: offer.slug,
          label: offer.preview,
          fallbackCode: offer.fallbackCode,
        });
      });
    },
    [editor, onClose, onSelectOffer],
  );

  const handleClose = useCallback(() => {
    onClose();
    setSearch("");
  }, [onClose]);

  return (
    <PickerPopover
      anchorEl={anchorEl}
      onClose={handleClose}
      paperSx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          placeholder="Search offers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          autoFocus
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Stack sx={{ overflow: "auto", px: 2, pb: 2 }} gap={2}>
        {filtered.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            {offers.length === 0 ? "No offers available" : "No matches"}
          </Typography>
        ) : (
          filtered.map((offer) => (
            <PickerRow
              key={offer.slug}
              primary={offer.name}
              secondary={offer.slug}
              onSelect={() => handleSelect(offer)}
              leading={<OfferSourceBadge isStatic={offer.isStatic} />}
            />
          ))
        )}
      </Stack>
    </PickerPopover>
  );
}
